import React, { useCallback, useContext, useEffect, useState } from 'react';
import { ActivityProps, ConfirmationModal, useModal } from '@europrocurement/flexy-components';
import { ModalCreateNote } from '@europrocurement/flexy-components/components/organisms/ModalCreateNote';
import { ModalEditNote } from '@europrocurement/flexy-components/components/organisms/ModalEditNote';
import { useFormaliteService } from '@europrocurement/l2d-domain';
import { useSnackbar } from 'notistack';
import { Box, useTheme } from '@mui/material';
import {
    LogActionDossierJsonldLogRead,
    type FproDossierNoteDossierReadNoteGroupTimestampableBlameambleGroup,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import useActionLogger from '@europrocurement/l2d-modules/hooks/useActionLogger';
import { UserJsonld } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import { IaButton } from '@europrocurement/l2d-modules/modules/IaServices';
import SelectedDossierContext from '../providers/SelectedDossierContext';
import { useUser } from '../../Utilisateur';
import IAMenu from '../../IaServices/components/IAMenu/IAMenu';

const NOTE_DELETE_MESSAGES = {
    question: 'Supprimer cette note ?',
    detail: {
        message: 'Cette action est irréversible. La note sera définitivement supprimée.',
        severity: 'warning' as const,
    },
    validation: 'Supprimer',
    annulation: 'Annuler',
};

/** Fonction pour récupérer un utilisateur par email */
type UseActivitiesHookProps = {
    actionOnSuccess?: () => void;
    defaultMemoClient?: boolean;
};
const useActivitiesHook = ({
    actionOnSuccess = undefined,
    defaultMemoClient = false,
}: UseActivitiesHookProps) => {
    const [isEditingSaving, setIsEditingSaving] = useState<boolean>(false);
    const [isDeletingSaving, setIsDeletingSaving] = useState<boolean>(false);
    const [activities, setActivities] = useState<ActivityProps[]>([]);
    const [activitiesLoading, setActivitiesLoading] = useState(true);
    const [withTechnicalLogs, setWithTechnicalLogs] = useState(false);

    const { modalActions } = useModal();
    const { dossierWithNotes, notesLoading, dossier, refreshDossierWithNotes } =
        useContext(SelectedDossierContext);
    const { dossierApi } = useFormaliteService();
    const { enqueueSnackbar } = useSnackbar();
    const { palette } = useTheme();
    const { fetchUser, cache } = useUser();
    const { getLogActionDossier } = useActionLogger();

    const renderIAMenu = (
        content: string,
        setContent: (value: React.SetStateAction<string>) => void,
    ) => (
        <Box sx={{ position: 'relative', zIndex: 2 }}>
            <IAMenu
                sxProps={{
                    position: 'absolute',
                    right: '5px',
                    top: '18px',
                }}
            >
                <IaButton
                    getParams={() => ({
                        textContent: content,
                    })}
                    onSuccess={(data) => {
                        const correctedText = data.answer;
                        if (correctedText) {
                            setContent(correctedText);
                            enqueueSnackbar('Correction effectuée');
                        }
                    }}
                />
                <IaButton
                    applicationName="L2D_Markdown"
                    label="Mettre en page avec Markdown"
                    getParams={() => ({
                        textContent: content,
                    })}
                    onSuccess={(data) => {
                        const correctedText = data.answer;
                        if (correctedText) {
                            setContent(correctedText);
                            enqueueSnackbar('Mise en page effectuée');
                        }
                    }}
                />
            </IAMenu>
        </Box>
    );

    /**
     * Gère l'ajout d'une nouvelle note
     */
    const handleAddNote = () => {
        modalActions.call(
            <ModalCreateNote
                renderIAMenu={renderIAMenu}
                defaultMemoClient={defaultMemoClient}
                onConfirm={async (content: string, highlighted: boolean, memoClient: boolean) => {
                    if (!dossier?.id) return;

                    try {
                        await dossierApi.apiDossiersIdNotesPost({
                            id: dossier?.id.toString(),
                            apiDossiersIdNotesPostRequest: {
                                content,
                                highlighted,
                                memoClient,
                            },
                        });

                        await refreshDossierWithNotes();
                        enqueueSnackbar(`Note créée avec succès`);
                        if (typeof actionOnSuccess !== 'undefined') {
                            actionOnSuccess();
                        } else {
                            modalActions.close();
                        }
                    } catch (error: unknown) {
                        console.error('Erreur lors de la création de la note:', error);
                        enqueueSnackbar(`Erreur lors de la création de la note`, {
                            variant: 'error',
                        });
                    }
                }}
                useMemoClient
            />,
        );
    };

    /**
     * Gère l'édition d'une note existante
     */
    const handleEditNote = (
        noteId: string,
        content: string,
        highlighted: boolean,
        memoClient: boolean,
    ) => {
        modalActions.call(
            <ModalEditNote
                initialContent={content}
                initialHighlighted={highlighted}
                initialMemoClient={memoClient}
                useMemoClient
                isSaving={isEditingSaving}
                renderIAMenu={renderIAMenu}
                onConfirm={async (
                    newContent: string,
                    newHighlighted: boolean,
                    newMemoClient: boolean,
                ) => {
                    if (!dossier?.id) return;

                    try {
                        setIsEditingSaving(true);

                        await dossierApi.apiDossiersIdNotesNoteIdPut({
                            id: dossier?.id,
                            noteId: +noteId.replace('note-', ''),
                            apiDossiersIdNotesPostRequest: {
                                content: newContent,
                                highlighted: newHighlighted,
                                memoClient: newMemoClient,
                            },
                        });

                        await refreshDossierWithNotes();

                        enqueueSnackbar(`Note mise à jour avec succès`);
                        if (typeof actionOnSuccess !== 'undefined') {
                            actionOnSuccess();
                        } else {
                            modalActions.close();
                        }
                    } catch (error) {
                        console.error('Erreur lors de la modification de la note:', error);

                        const isUnauthorized =
                            (error &&
                                typeof error === 'object' &&
                                'status' in error &&
                                error.status === 403) ||
                            (error &&
                                typeof error === 'object' &&
                                'response' in error &&
                                error.response &&
                                typeof error.response === 'object' &&
                                'status' in error.response &&
                                error.response.status === 403);

                        if (isUnauthorized) {
                            enqueueSnackbar(`Vous n'avez pas les droits pour modifier cette note`, {
                                variant: 'error',
                            });
                        } else {
                            enqueueSnackbar(`Erreur lors de la modification de la note`, {
                                variant: 'error',
                            });
                        }
                    } finally {
                        setIsEditingSaving(false);
                    }
                }}
                onCancel={() => modalActions.close()}
            />,
        );
    };

    /**
     * Gère la suppression d'une note
     */
    const handleDeleteNote = (noteId: string) => {
        modalActions.call(
            <ConfirmationModal
                messages={NOTE_DELETE_MESSAGES}
                color={palette.error}
                disabled={isDeletingSaving}
                actionOnValidation={async () => {
                    if (!dossier?.id) return;

                    try {
                        setIsDeletingSaving(true);

                        await dossierApi.apiDossiersIdNotesNoteIdDelete({
                            id: dossier?.id,
                            noteId: +noteId.replace('note-', ''),
                        });

                        await refreshDossierWithNotes();
                        enqueueSnackbar(`Note supprimée avec succès`);

                        if (typeof actionOnSuccess !== 'undefined') {
                            actionOnSuccess();
                        } else {
                            modalActions.close();
                        }
                    } catch (error) {
                        console.error('Erreur lors de la suppression de la note:', error);

                        const isUnauthorized =
                            (error &&
                                typeof error === 'object' &&
                                'status' in error &&
                                error.status === 403) ||
                            (error &&
                                typeof error === 'object' &&
                                'response' in error &&
                                error.response &&
                                typeof error.response === 'object' &&
                                'status' in error.response &&
                                error.response.status === 403);

                        if (isUnauthorized) {
                            enqueueSnackbar(
                                `Vous n'avez pas les droits pour supprimer cette note`,
                                {
                                    variant: 'error',
                                },
                            );
                        } else {
                            enqueueSnackbar(`Erreur lors de la suppression de la note`, {
                                variant: 'error',
                            });
                        }
                    } finally {
                        setIsDeletingSaving(false);
                    }
                }}
                actionOnCancellation={() => modalActions.close()}
            />,
        );
    };

    /**
     * Formate le nom et prénom d'un utilisateur selon les conventions d'affichage
     */
    const formatUserName = (
        prenom?: string,
        nom?: string,
    ): { firstName: string; lastName: string } => {
        if (!prenom || !nom) {
            return { firstName: '', lastName: '' };
        }

        return {
            firstName: prenom.charAt(0).toUpperCase() + prenom.slice(1).toLowerCase(),
            lastName: nom.toUpperCase(),
        };
    };

    /**
     * Résout les informations utilisateur à partir d'un email avec mise en cache
     */
    const resolveUserInfo = useCallback(
        async (userKeycloak: string): Promise<UserJsonld | null> => {
            if (cache[userKeycloak] !== undefined) {
                return cache[userKeycloak];
            }
            try {
                const user = await fetchUser(userKeycloak);
                return user;
            } catch {
                return null;
            }
        },
        [fetchUser, cache],
    );
    /**
     * Convertit une note de dossier en activité avec résolution des informations utilisateur
     */
    const createNoteActivity = useCallback(
        async (
            note: FproDossierNoteDossierReadNoteGroupTimestampableBlameambleGroup,
        ): Promise<ActivityProps> => {
            const createdByUser = note.createdBy ? await resolveUserInfo(note.createdBy) : null;
            const updatedByUser = note.updatedBy ? await resolveUserInfo(note.updatedBy) : null;

            const createdByFormatted = createdByUser
                ? formatUserName(createdByUser?.firstName ?? '', createdByUser?.lastName ?? '')
                : { firstName: note.createdBy || 'Anonyme', lastName: '' };

            const updatedByFormatted = updatedByUser
                ? formatUserName(updatedByUser.firstName ?? '', updatedByUser.lastName ?? '')
                : { firstName: note.updatedBy || '', lastName: '' };

            // TODO: Il faudra changer ça par le keycloakUserId une fois que les dernières mises à jour seront merge. En attendant, on utilise le preferred_username comme identifiant pour récupérer l'avatar.
            const userId = createdByUser?.username || note.createdBy || 'unknown';

            return {
                id: `note-${note.id}`,
                type: 'note',
                noteType: note.highlighted ? 'important' : 'standard',
                content: note.content || '',
                createdAt: note.createdAt ? new Date(note.createdAt) : new Date(),
                userId: typeof userId === 'string' ? userId : String(userId),
                userFirstName: createdByFormatted.firstName,
                userLastName: createdByFormatted.lastName,
                updatedAt: note.updatedAt ? new Date(note.updatedAt) : undefined,
                updatedBy: note.updatedBy || undefined,
                updatedByFirstName: updatedByFormatted.firstName,
                updatedByLastName: updatedByFormatted.lastName,
                memoClient: note.memoClient || false,
            };
        },
        [resolveUserInfo],
    );

    /**
     * Convertit un log en activité
     */
    const createLogActivity = useCallback(
        async (item: LogActionDossierJsonldLogRead): Promise<ActivityProps> => {
            const uid = item.idUserKeycloak ?? '';
            const userFromCache = uid ? await resolveUserInfo(uid) : null;

            return {
                type: 'action',
                userFirstName: uid ? (userFromCache?.firstName ?? uid) : '',
                userLastName: uid ? (userFromCache?.lastName ?? uid) : '',
                actionType: item.actionType?.logCategories?.[0]?.category ?? '',
                createdAt: item.date ? new Date(item.date as string) : new Date(),
                id: String(item['@id'] ?? 0),
                objectType: item.actionType?.actionTypeLibelle ?? '',
                description: item.logMessage ?? '',
                memoClient: false, // Default value for logs
            };
        },
        [resolveUserInfo],
    );

    /**
     * Ajoute les activités de logs et de notes
     */
    const addActivities = useCallback(
        async (dataLogs: LogActionDossierJsonldLogRead[]) => {
            const logActivities = await Promise.all(dataLogs.map(createLogActivity));
            const noteActivities = await Promise.all(
                (dossierWithNotes.notes || []).map(createNoteActivity),
            );

            const allActivities = [...logActivities, ...noteActivities].sort(
                (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
            );

            return allActivities;
        },
        [createLogActivity, createNoteActivity, dossierWithNotes.notes],
    );

    /**
     * Charge les logs et les notes
     */
    const loadAllActivities = useCallback(async () => {
        if (!dossier?.id) return;

        try {
            const logsData = await getLogActionDossier(dossier.id, false, withTechnicalLogs);
            const allActivities = await addActivities(logsData);
            setActivities(allActivities);
        } catch (error) {
            console.error('Erreur lors du chargement des activités:', error);
        } finally {
            setActivitiesLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier?.id, withTechnicalLogs, addActivities]);

    /**
     * Recharger avec les logs techniques si demandé
     */
    const loadActivitiesTechniques = (value: boolean) => {
        setWithTechnicalLogs(value);
    };

    useEffect(() => {
        if (!notesLoading) {
            loadAllActivities();
        }
    }, [loadAllActivities, notesLoading, dossierWithNotes.notes]);

    return {
        activities,
        activitiesLoading,
        handleAddNote,
        handleEditNote,
        handleDeleteNote,
        loadActivitiesTechniques,
    };
};

export default useActivitiesHook;
